import { create } from 'zustand'

type IssueId = string | number

// Issue ids cross two boundaries: the raw API row carries a numeric id, while
// `useMapIssues` (used by the rows table) emits the same id stringified. If
// each caller adds a different shape to the Set, lookups silently miss
// (`Set.has(86)` returns false when the Set holds "86" — strict equality).
// Normalize at every entry point so callers stay agnostic.
const norm = (id: IssueId): string => String(id)

type BulkSelectStore = {
  // Always holds canonical (string) ids so look-ups from any caller match.
  selected: Set<string>
  isSelectionMode: boolean
  // True while `useBulkEditMutation` is fanning out PATCH/DELETE calls.
  // Read by `useIssuesSubscriber` (via `getState()`) to skip the per-event
  // list invalidation — otherwise every successful PATCH triggers a Mercure
  // broadcast which races a refetch against the still-in-flight loop and
  // overwrites the optimistic UI with whatever server-truth the API can
  // assemble at that moment. The mutation hook flips it back off in
  // `onSettled` and runs a single explicit invalidate then.
  isBulkInFlight: boolean
  setBulkInFlight: (value: boolean) => void
  // The id of the last item that was toggled by a plain click (no shift).
  // Used as the anchor for shift-click range selection — clicking item A then
  // shift-clicking item B extends the selection to every id between them in
  // the supplied `orderedIds`. Cleared on `clear()` and on any range select
  // so the next plain click re-anchors.
  anchorId: string | null
  toggle: (id: IssueId) => void
  select: (id: IssueId) => void
  deselect: (id: IssueId) => void
  setMany: (ids: IssueId[]) => void
  addMany: (ids: IssueId[]) => void
  removeMany: (ids: IssueId[]) => void
  // Range-select from the current anchor (or `id` itself if no anchor) to
  // `id` within `orderedIds`. The whole range is added to the selection
  // (additive — Linear-style). If `id` isn't in `orderedIds`, falls back to
  // toggling `id` alone so the user still gets feedback.
  selectRange: (id: IssueId, orderedIds: IssueId[]) => void
  clear: () => void
  isSelected: (id: IssueId) => boolean
}

export const useBulkSelectStore = create<BulkSelectStore>((set, get) => ({
  selected: new Set<string>(),
  isSelectionMode: false,
  anchorId: null,
  isBulkInFlight: false,
  setBulkInFlight: value => set({ isBulkInFlight: value }),
  toggle: id =>
    set(state => {
      const key = norm(id)
      const next = new Set(state.selected)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return {
        selected: next,
        isSelectionMode: next.size > 0,
        // Re-anchor to whatever the user just clicked, so the next shift-click
        // ranges from here.
        anchorId: key,
      }
    }),
  select: id =>
    set(state => {
      const key = norm(id)
      if (state.selected.has(key)) return state
      const next = new Set(state.selected)
      next.add(key)
      return { selected: next, isSelectionMode: true, anchorId: key }
    }),
  deselect: id =>
    set(state => {
      const key = norm(id)
      if (!state.selected.has(key)) return state
      const next = new Set(state.selected)
      next.delete(key)
      return {
        selected: next,
        isSelectionMode: next.size > 0,
        anchorId: key,
      }
    }),
  setMany: ids =>
    set(() => {
      const next = new Set<string>(ids.map(norm))
      return {
        selected: next,
        isSelectionMode: next.size > 0,
        anchorId: ids.length > 0 ? norm(ids[ids.length - 1]) : null,
      }
    }),
  addMany: ids =>
    set(state => {
      if (ids.length === 0) return state
      const next = new Set(state.selected)
      for (const id of ids) next.add(norm(id))
      if (next.size === state.selected.size) return state
      return { selected: next, isSelectionMode: next.size > 0 }
    }),
  removeMany: ids =>
    set(state => {
      if (ids.length === 0 || state.selected.size === 0) return state
      const next = new Set(state.selected)
      let changed = false
      for (const id of ids) {
        if (next.delete(norm(id))) changed = true
      }
      if (!changed) return state
      return { selected: next, isSelectionMode: next.size > 0 }
    }),
  selectRange: (id, orderedIds) =>
    set(state => {
      const key = norm(id)
      const normalized = orderedIds.map(norm)
      const endIdx = normalized.indexOf(key)
      if (endIdx === -1) {
        // Unknown id — degrade to a plain toggle so the click still does
        // something predictable.
        const next = new Set(state.selected)
        if (next.has(key)) next.delete(key)
        else next.add(key)
        return {
          selected: next,
          isSelectionMode: next.size > 0,
          anchorId: key,
        }
      }
      const anchor = state.anchorId
      const startIdx = anchor == null ? endIdx : normalized.indexOf(anchor)
      const lo = Math.min(startIdx === -1 ? endIdx : startIdx, endIdx)
      const hi = Math.max(startIdx === -1 ? endIdx : startIdx, endIdx)
      const next = new Set(state.selected)
      for (let i = lo; i <= hi; i++) next.add(normalized[i])
      return { selected: next, isSelectionMode: next.size > 0 }
    }),
  clear: () => set({ selected: new Set<string>(), isSelectionMode: false, anchorId: null }),
  isSelected: id => get().selected.has(norm(id)),
}))

export const useIsSelected = (id: IssueId) => useBulkSelectStore(s => s.selected.has(norm(id)))
